import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BrainCircuit,
  ChevronDown,
  ChevronLeft,
  Eye,
  Gauge,
  Heart,
  History,
  LayoutGrid,
  Lightbulb,
  Lock,
  MessageSquare,
  Plus,
  Radar,
  ShieldCheck,
  Sparkles,
  Star,
  Swords,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  aiAdviceLabel,
  canViewCommunity,
  formatCountdownDetailed,
  formatTimeSince,
  isMatchStarted,
  isOwnCommunityTip,
  normalizePercent,
} from "../../../utils/predictionHelpers";
import { buildOutcomeAgreement } from "../desk/tipsDeskHelpers";
import { formatAppDate, formatAppTime } from "../../../utils/appTimeFormat";
import FavoriteTeamStar from "../../p2p/FavoriteTeamStar";
import CreatorRecord from "../CreatorRecord";
import ConfidencePieRing from "../ConfidencePieRing";
import OutcomeStatusBadge from "../OutcomeStatusBadge";
import ShareIconButton from "../../ShareIconButton";
import { fixtureHasFavoriteTeam, teamMatchesFavorite } from "../../../utils/p2pHelpers";
import {
  buildCommunityPredictionShareLink,
  buildCommunityPredictionShareText,
  buildPredictionFixtureShareLink,
  buildPredictionFixtureShareText,
} from "../../../utils/predictionShareHelpers";
import "../../../share-icon.css";

const pct = (value) => `${Math.max(0, Math.min(100, Number(value) || 0))}%`;
const clean = (value) => String(value ?? "").replace("-", "");

const p2pFixtureForEntry = (entry) => {
  const fixture = entry?.fixture || {};
  const fixtureId = entry?.fixtureId || fixture?._id || fixture?.id;
  const timestamp = fixture?.timestamp || entry?.ts || null;
  const timestampNumber = Number(timestamp);
  const date =
    fixture?.date ||
    (Number.isFinite(timestampNumber) && timestampNumber > 0
      ? new Date(timestampNumber > 1e12 ? timestampNumber : timestampNumber * 1000).toISOString()
      : undefined);
  return {
    ...fixture,
    _id: fixtureId,
    id: fixtureId,
    date,
    timestamp,
    homeTeam: fixture?.homeTeam || fixture?.teams?.home || {},
    awayTeam: fixture?.awayTeam || fixture?.teams?.away || {},
  };
};

const p2pLeagueForEntry = (entry) => {
  const league = entry?.league || entry?.fixture?.league || {};
  return {
    id: league?._id || league?.id,
    name: league?.name || "League",
    logo: league?.logo || null,
    country: league?.country?.name || league?.country || "",
  };
};

const leadOutcome = (tip) => {
  const outcomes = tip?.outcomes || [];
  if (!outcomes.length) return null;
  return [...outcomes].sort(
    (a, b) => (Number(b.confidence) || 0) - (Number(a.confidence) || 0)
  )[0];
};

/** Two clearly separated source cards — AI vs Crowd — never blended into one score. */
const SourceCompare = ({ entry, activeSource, onSelectSource, agreement }) => {
  const hasAI = Boolean(entry.ai);
  const crowdCount = entry.consensus?.count || 0;
  if (!hasAI && !crowdCount) return null;

  const aiLocked = hasAI && !entry.ai?.isUnlocked;
  const aiPick = aiLocked ? "Locked" : agreement?.aiPick || entry.aiReadout?.pick || "—";
  const aiConf = agreement?.aiConfidence || entry.aiReadout?.confidence || 0;
  const readableCrowd = agreement?.readableCount ?? entry.consensus?.readableCount ?? 0;
  const crowdPick =
    agreement?.topCrowdPick ||
    entry.consensus?.topPick ||
    (crowdCount && readableCrowd === 0 ? "Premium sealed" : "Mixed");
  const crowdAgree = agreement?.tipsterAgreement || entry.consensus?.agreement || 0;
  const both = hasAI && crowdCount;

  const edge =
    agreement?.aiCrowd === "agree"
      ? { tone: "agree", label: "Same pick" }
      : agreement?.aiCrowd === "split"
        ? { tone: "split", label: "Different picks" }
        : both
          ? { tone: "none", label: "Compare" }
          : null;

  return (
    <section
      className={`pr-source-compare ${both ? "" : "pr-source-compare-solo"}`}
      aria-label="AI and crowd predictions"
    >
      <div className={`pr-source-compare-grid ${both ? "" : "pr-source-compare-grid-solo"}`}>
        {hasAI ? (
          <button
            type="button"
            className={`pr-source-card pr-source-card-ai ${activeSource === "ai" ? "pr-source-card-on" : ""}`}
            onClick={() => onSelectSource?.("ai")}
            aria-pressed={activeSource === "ai"}
          >
            <span className="pr-source-card-head">
              <BrainCircuit size={13} aria-hidden />
              AI model
            </span>
            <strong className="pr-source-card-pick">{aiPick}</strong>
            <em className="pr-source-card-meta">
              {aiLocked ? "Unlock to reveal" : `${aiConf}% model confidence`}
            </em>
          </button>
        ) : null}

        {both && edge ? (
          <div className={`pr-source-vs pr-source-vs-${edge.tone}`} aria-hidden>
            <span>{edge.label}</span>
          </div>
        ) : null}

        {crowdCount ? (
          <button
            type="button"
            className={`pr-source-card pr-source-card-crowd ${activeSource === "crowd" ? "pr-source-card-on" : ""}`}
            onClick={() => onSelectSource?.("crowd")}
            aria-pressed={activeSource === "crowd"}
          >
            <span className="pr-source-card-head">
              <Users size={13} aria-hidden />
              Crowd tips
            </span>
            <strong className="pr-source-card-pick">{crowdPick}</strong>
            <em className="pr-source-card-meta">
              {crowdCount} tipster{crowdCount > 1 ? "s" : ""}
              {readableCrowd === 0
                ? " · unlock to reveal"
                : crowdAgree
                  ? ` · ${crowdAgree}% agreement`
                  : ""}
            </em>
          </button>
        ) : null}
      </div>
    </section>
  );
};

/** Outcome board: what tipsters picked, how much they agree, and where AI lands. */
const OutcomeAgreementBoard = ({ agreement }) => {
  if (!agreement) return null;
  const {
    rows,
    tipsterCount,
    readableCount,
    lockedCount,
    tipsterAgreement,
    topCrowdPick,
    aiPick,
    aiConfidence,
    aiLocked,
    hasAI,
    aiCrowd,
    alignedShare,
    alignedCount,
  } = agreement;

  if (!tipsterCount) return null;

  const statusCopy =
    aiCrowd === "agree"
      ? `AI and tipsters agree on ${topCrowdPick}`
      : aiCrowd === "split"
        ? `AI picks ${aiPick}; tipsters lean ${topCrowdPick}`
        : aiCrowd === "ai-only"
          ? "AI has a pick — no readable tipster picks yet"
          : aiCrowd === "crowd-only"
            ? "Tipster lean only — no AI pick to compare"
            : lockedCount && !readableCount
              ? "Premium tips sealed — unlock to see tipster outcomes"
              : "Outcome agreement";

  return (
    <section className="pr-agree-board" aria-label="Outcome agreement">
      <div className="pr-agree-board-head">
        <span>
          <Users size={13} aria-hidden />
          Outcome agreement
        </span>
        <em>
          {readableCount}/{tipsterCount} tipster{tipsterCount === 1 ? "" : "s"} readable
          {hasAI ? " · AI compared" : ""}
        </em>
      </div>

      <p className={`pr-agree-status pr-agree-status-${aiCrowd || "none"}`}>{statusCopy}</p>

      <div className="pr-agree-metrics">
        <div>
          <em>Tipster agreement</em>
          <strong>{readableCount ? `${tipsterAgreement}%` : "—"}</strong>
          <b>{topCrowdPick ? `on ${topCrowdPick}` : "no open picks"}</b>
        </div>
        <div>
          <em>AI ↔ tipsters</em>
          <strong>
            {aiLocked
              ? "Locked"
              : aiCrowd === "agree"
                ? "Aligned"
                : aiCrowd === "split"
                  ? "Split"
                  : "—"}
          </strong>
          <b>
            {aiCrowd === "agree"
              ? `${alignedCount} tipster${alignedCount === 1 ? "" : "s"} · ${alignedShare}%`
              : aiPick
                ? `AI ${aiConfidence}%`
                : "no AI pick"}
          </b>
        </div>
      </div>

      {rows.length ? (
        <div className="pr-agree-rows">
          <div className="pr-agree-row pr-agree-row-head">
            <span>Outcome</span>
            <span>Tipsters</span>
            <span>Share</span>
            <span>AI</span>
          </div>
          {rows.map((row) => (
            <div
              key={row.outcome}
              className={`pr-agree-row ${row.aiMatch ? "pr-agree-row-ai" : ""} ${row.aiOnly ? "pr-agree-row-ai-only" : ""}`}
            >
              <strong>{row.outcome}</strong>
              <span className="pr-agree-tipsters">
                {row.count > 0 ? (
                  <>
                    <b>{row.count}</b>
                    <i>
                      {row.tipsters
                        .slice(0, 3)
                        .map((t) => t.name)
                        .join(", ")}
                      {row.tipsters.length > 3 ? ` +${row.tipsters.length - 3}` : ""}
                    </i>
                  </>
                ) : (
                  <em>None open</em>
                )}
              </span>
              <span className="pr-agree-share">
                {row.aiOnly ? (
                  <em>—</em>
                ) : (
                  <>
                    <div className="pr-agree-track">
                      <i style={{ width: `${row.share}%` }} />
                    </div>
                    <b>{row.share}%</b>
                    <em>{row.avgConfidence}% avg</em>
                  </>
                )}
              </span>
              <span className={`pr-agree-ai ${row.aiMatch ? "pr-agree-ai-on" : ""}`}>
                {aiLocked ? (
                  <em>Locked</em>
                ) : row.aiMatch ? (
                  <>
                    <BrainCircuit size={11} aria-hidden />
                    <b>{aiConfidence}%</b>
                  </>
                ) : (
                  <em>—</em>
                )}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="pr-empty-copy">
          {lockedCount
            ? `${lockedCount} premium tip${lockedCount > 1 ? "s" : ""} locked — unlock below to map tipster outcomes.`
            : "No tipster outcomes to compare yet."}
        </p>
      )}

      <p className="pr-agree-note">
        Tipster agreement uses readable tips only. Premium picks stay sealed until unlocked. Your own tips always count.
      </p>
    </section>
  );
};

/** One tipster = one expandable slip with full outcomes / analysis / unlock. */
const TipsterSlip = ({
  tip,
  fixture,
  userId,
  matchStarted,
  expanded,
  onToggle,
  onUnlock,
  unlockPending,
  isAuthenticated,
  onNotify,
}) => {
  const mine = isOwnCommunityTip(tip, userId);
  const visible = canViewCommunity(tip, userId);
  const lead = leadOutcome(tip);
  const outcomes = [...(tip.outcomes || [])].sort(
    (a, b) => (Number(b.confidence) || 0) - (Number(a.confidence) || 0)
  );
  const premium = Boolean(tip.isPremium);
  const locked = premium && !visible;
  const price = Number(tip.price) || 0;
  const busy = String(unlockPending || "") === String(tip._id || "");
  const creator = tip.user?.name || "Tipster";
  const accent = locked ? "locked" : premium ? "premium" : "free";
  const shareTip = tip?.fixture?.teams ? tip : { ...tip, fixture: tip.fixture || fixture };

  const handleUnlock = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!isAuthenticated || matchStarted || busy || !locked) return;
    onUnlock?.(tip._id);
  };

  return (
    <article
      className={`pr-tipster-slip ${expanded ? "pr-tipster-slip-open" : ""} ${locked ? "pr-tipster-slip-locked" : ""}`}
    >
      <div className="pr-tipster-slip-main">
        <i className={`pr-tipster-slip-accent pr-tipster-slip-accent-${accent}`} aria-hidden />

        <button
          type="button"
          className="pr-tipster-slip-hit"
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <span className="pr-tipster-slip-avatar" aria-hidden>
            {tip.user?.profileImage ? (
              <img src={tip.user.profileImage} alt="" />
            ) : (
              creator.slice(0, 1).toUpperCase()
            )}
          </span>

          <span className="pr-tipster-slip-body">
            <span className="pr-tipster-slip-line1">
              <strong>{mine ? "You" : creator}</strong>
              {mine ? (
                <span className="pr-tipster-badge pr-tipster-badge-mine">Yours</span>
              ) : null}
              <span className={`pr-tipster-badge pr-tipster-badge-${accent}`}>
                {locked ? (
                  <>
                    <Lock size={8} />
                    Locked
                  </>
                ) : premium ? (
                  <>
                    <Star size={8} />
                    Premium
                  </>
                ) : (
                  <>
                    <Sparkles size={8} />
                    Free
                  </>
                )}
              </span>
              <em>{formatTimeSince(tip.createdAt)}</em>
            </span>
            <CreatorRecord user={tip.user} compact className="pr-tipster-score" />
            <span className="pr-tipster-slip-pickline">
              {visible ? (
                <>
                  <b>{lead?.outcome || "No lead pick"}</b>
                  {lead ? <i>{Number(lead.confidence) || 0}%</i> : null}
                  {outcomes.length > 1 ? <em>+{outcomes.length - 1} more</em> : null}
                </>
              ) : (
                <b className="pr-tipster-slip-sealed">
                  <Lock size={10} />
                  Premium tip sealed
                  {price > 0 ? ` · ${price} COIN` : ""}
                </b>
              )}
            </span>
          </span>
        </button>

        <span className="pr-tipster-slip-action">
          {locked && !matchStarted ? (
            isAuthenticated ? (
              <button
                type="button"
                className="pr-slip-cta pr-slip-cta-unlock"
                disabled={busy}
                onClick={handleUnlock}
              >
                {busy ? "…" : price > 0 ? `${price} COIN` : "Unlock"}
              </button>
            ) : (
              <Link
                className="pr-slip-cta pr-slip-cta-unlock"
                to="/app/login"
                state={{ from: "/app/predictions" }}
                onClick={(e) => e.stopPropagation()}
              >
                Login
              </Link>
            )
          ) : (
            <button type="button" className="pr-slip-cta" onClick={onToggle}>
              {expanded ? "Hide" : "Read"}
            </button>
          )}
          <button
            type="button"
            className="pr-tipster-chevron-btn"
            onClick={onToggle}
            aria-label={expanded ? "Hide tip details" : "Show tip details"}
          >
            <ChevronDown size={12} className={expanded ? "pr-tipster-chevron-open" : ""} aria-hidden />
          </button>
        </span>
      </div>

      <div className="pr-tipster-micro">
        <span>
          <Heart size={10} />
          {tip.likes?.length || 0}
        </span>
        <span>
          <Eye size={10} />
          {tip.views || 0}
        </span>
        <span>
          {locked
            ? "Picks hidden"
            : `${outcomes.length} outcome${outcomes.length === 1 ? "" : "s"}`}
        </span>
        <ShareIconButton
          className="bb-share-icon-sm bb-share-icon-ghost pr-tipster-share"
          size={11}
          url={buildCommunityPredictionShareLink(tip._id)}
          text={buildCommunityPredictionShareText(shareTip)}
          title="BetBond Tip"
          ariaLabel="Share this tip"
          onShared={(mode) =>
            onNotify?.({
              message: mode === "copied" ? "Link copied." : "Shared.",
            })
          }
          onError={(message) => onNotify?.({ message })}
        />
      </div>

      {expanded ? (
        <div className="pr-tipster-drawer">
          {locked ? (
            <div className="pr-tipster-locked">
              <Lock size={16} />
              <strong>{matchStarted ? "Sealed — match started" : "Premium tip locked"}</strong>
              <p>
                {matchStarted
                  ? "This tip can no longer be unlocked."
                  : `Pay to unlock ${creator}'s picks, per-outcome notes, and betting advice. You will not see the prediction until you unlock.`}
              </p>
              {!matchStarted ? (
                isAuthenticated ? (
                  <button type="button" disabled={busy} onClick={handleUnlock}>
                    {busy ? "Unlocking…" : price > 0 ? `Unlock · ${price} COIN` : "Unlock"}
                  </button>
                ) : (
                  <Link className="pr-tipster-login" to="/app/login" state={{ from: "/app/predictions" }}>
                    Log in to unlock
                  </Link>
                )
              ) : null}
            </div>
          ) : (
            <>
              <CreatorRecord user={tip.user} expanded className="pr-tipster-score-card" />
              {mine && premium ? (
                <p className="pr-tipster-owner-note">Your premium tip — visible to you without unlocking.</p>
              ) : null}
              {lead ? (
                <div className="pr-tipster-hero">
                  <div className="pr-tipster-hero-copy">
                    <span>Top pick · {Number(lead.confidence) || 0}%</span>
                    <strong>
                      {lead.outcome}
                      <OutcomeStatusBadge status={lead.status} />
                    </strong>
                    {lead.analysis ? <p>{lead.analysis}</p> : null}
                  </div>
                </div>
              ) : null}

              {outcomes.length ? (
                <div className="pr-tipster-outcomes">
                  <OutcomeDetails outcomes={outcomes} locked={false} />
                </div>
              ) : null}

              {tip.bettingAdvice ? (
                <blockquote className="pr-tipster-advice">
                  <Lightbulb size={12} aria-hidden />
                  <div>
                    <span>Betting advice</span>
                    <p>{tip.bettingAdvice}</p>
                  </div>
                </blockquote>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </article>
  );
};

const TeamCrest = ({ team, align, favoriteTeam }) => {
  const isFavorite = teamMatchesFavorite(team, favoriteTeam);
  return (
  <div className={`pr-crest pr-crest-${align} ${isFavorite ? "pr-crest-fav" : ""}`}>
    <span className="pr-crest-orb">
      {team?.logo ? <img src={team.logo} alt="" /> : <i>{(team?.name || "?").slice(0, 1)}</i>}
      {isFavorite ? <FavoriteTeamStar size={8} className="pr-crest-star" /> : null}
    </span>
    <strong>{team?.name || (align === "home" ? "Home" : "Away")}</strong>
  </div>
  );
};

const ProbabilityLens = ({ percent, homeName, awayName, locked, calls }) => {
  const normalized = normalizePercent(percent || {});
  const values = [
    { key: "home", label: homeName, value: normalized.home },
    { key: "draw", label: "Draw", value: normalized.draw },
    { key: "away", label: awayName, value: normalized.away },
  ];
  const leader = values.reduce((best, item) => (item.value > best.value ? item : best), values[0]);

  return (
    <div className={`pr-prob-lens ${locked ? "pr-prob-lens-locked" : ""}`}>
      <ConfidencePieRing
        value={locked ? 0 : leader?.value || 0}
        size={124}
        innerRatio={0.6}
        animate={!locked}
        className="pr-prob-core-ring"
        innerClassName="pr-prob-core-inner"
      >
        <span>Likely</span>
        <strong>{locked ? "Locked" : leader?.label || "—"}</strong>
        <em>{locked ? "Unlock" : `${leader?.value || 0}%`}</em>
      </ConfidencePieRing>
      <div className="pr-prob-side">
        <div className="pr-prob-bars">
          {values.map((item) => (
            <div key={item.key} className={`pr-prob-row pr-prob-row-${item.key}`}>
              <span>{item.label}</span>
              <div>
                <i style={{ width: locked ? "30%" : pct(item.value) }} />
              </div>
              <strong>{locked ? "--" : `${item.value}%`}</strong>
            </div>
          ))}
        </div>
        {calls ? (
          <div className={`pr-calls ${locked ? "pr-calls-locked" : ""}`}>
            <span>
              <em>Score</em>
              <strong>{locked ? "—" : calls.score || "—"}</strong>
            </span>
            <span>
              <em>Goals</em>
              <strong>{locked ? "—" : calls.goals || "—"}</strong>
            </span>
            <span>
              <em>Win/Draw</em>
              <strong>{locked ? "—" : calls.winDraw}</strong>
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const CompareBar = ({ label, home, away }) => {
  const h = Number.parseFloat(home) || 0;
  const a = Number.parseFloat(away) || 0;
  const total = h + a || 1;
  return (
    <div className="pr-cbar">
      <span className="pr-cbar-h">{h}</span>
      <div className="pr-cbar-track">
        <i style={{ width: `${(h / total) * 100}%` }} />
        <b style={{ width: `${(a / total) * 100}%` }} />
      </div>
      <span className="pr-cbar-a">{a}</span>
      <em>{label}</em>
    </div>
  );
};

const FormDots = ({ team, label }) => {
  const chars = String(team?.form || team?.last5?.form || "").split("").slice(-6);
  return (
    <div className="pr-form-row">
      <span>{label}</span>
      <div>
        {chars.length ? (
          chars.map((c, i) => <i key={`${c}-${i}`} className={`pr-dot-${c.toLowerCase()}`}>{c}</i>)
        ) : (
          <em>—</em>
        )}
      </div>
    </div>
  );
};

const StatCell = ({ label, value }) => (
  <span className="pr-stat-cell">
    <em>{label}</em>
    <strong>{value ?? "—"}</strong>
  </span>
);

const EvidenceBoard = ({ prediction, context, home, away }) => {
  const source = prediction || context || {};
  const hasAnyContext = Boolean(prediction || context?.homeTeam || context?.awayTeam || context?.standings);

  if (!hasAnyContext) {
    return (
      <section className="pr-panel pr-evidence-empty">
        <div className="pr-panel-head">
          <Radar size={14} />
          <span>Evidence</span>
        </div>
        <p>No team evidence is attached to this fixture yet.</p>
      </section>
    );
  }

  const comparison = source.comparison || {};
  const standings = source.standings || {};
  const h2h = source.h2h || [];
  const homeData = home || source.homeTeam || {};
  const awayData = away || source.awayTeam || {};
  const homeName = homeData?.name || "Home";
  const awayName = awayData?.name || "Away";
  const hasComparison = Object.values(comparison).some((value) => value?.home || value?.away);
  const rows = [
    ["Form", comparison.form?.home, comparison.form?.away],
    ["Attack", comparison.att?.home, comparison.att?.away],
    ["Defense", comparison.def?.home, comparison.def?.away],
    ["Poisson", comparison.poissonDistribution?.home, comparison.poissonDistribution?.away],
    ["H2H", comparison.h2h?.home, comparison.h2h?.away],
    ["Goals", comparison.goals?.home, comparison.goals?.away],
  ];

  return (
    <>
      <section className="pr-panel pr-edge-panel">
        <div className="pr-panel-head">
          <Radar size={14} />
          <span>{prediction ? "Evidence matrix" : "Fixture context"}</span>
          <em>{homeName} vs {awayName}</em>
        </div>
        {hasComparison ? (
          <div className="pr-cbar-grid">
            {rows.map(([label, h, a]) => (
              <CompareBar key={label} label={label} home={h} away={a} />
            ))}
          </div>
        ) : (
          <p className="pr-empty-copy">No comparison vector. Team form and table below carry the context.</p>
        )}
      </section>

      <section className="pr-secondary-grid">
        <div className="pr-panel">
          <div className="pr-panel-head">
            <TrendingUp size={14} />
            <span>Form</span>
          </div>
          <FormDots team={homeData} label={homeName} />
          <FormDots team={awayData} label={awayName} />
          <div className="pr-stat-grid">
            <StatCell label="GF" value={homeData?.last5?.goalsFor ?? homeData?.goals?.for?.total?.total} />
            <StatCell label="CS" value={homeData?.cleanSheet?.total} />
            <StatCell label="FTS" value={homeData?.failedToScore?.total} />
            <StatCell label="GF" value={awayData?.last5?.goalsFor ?? awayData?.goals?.for?.total?.total} />
            <StatCell label="CS" value={awayData?.cleanSheet?.total} />
            <StatCell label="FTS" value={awayData?.failedToScore?.total} />
          </div>
        </div>

        <div className="pr-panel">
          <div className="pr-panel-head">
            <ShieldCheck size={14} />
            <span>Table</span>
          </div>
          <div className="pr-standings">
            {[[homeName, standings.home], [awayName, standings.away]].map(([name, row]) => (
              <div key={name}>
                <strong>{name}</strong>
                <div>
                  <StatCell label="Rank" value={row?.rank} />
                  <StatCell label="Pts" value={row?.points} />
                  <StatCell label="GD" value={row?.goalsDiff} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="pr-panel">
          <div className="pr-panel-head">
            <History size={14} />
            <span>H2H</span>
          </div>
          <div className="pr-h2h-strip">
            {h2h.length ? (
              h2h.slice(0, 6).map((match, i) => (
                <span key={`${match.fixtureId}-${i}`}>
                  <strong>{match.goals?.home ?? 0}:{match.goals?.away ?? 0}</strong>
                </span>
              ))
            ) : (
              <p className="pr-empty-copy">No sample.</p>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

const OutcomeDetails = ({ outcomes, locked }) => {
  if (locked) return null;
  const sorted = [...(outcomes || [])].sort(
    (a, b) => (Number(b.confidence) || 0) - (Number(a.confidence) || 0)
  );
  if (!sorted.length) return null;

  return (
    <div className="pr-outcome-details">
      {sorted.map((item, index) => (
        <article key={`${item.outcome}-${index}`} className="pr-outcome-detail">
          <div className="pr-outcome-detail-head">
            <strong>{item.outcome}</strong>
            <span className="pr-outcome-detail-end">
              <OutcomeStatusBadge status={item.status} />
              <b>{Number(item.confidence) || 0}%</b>
            </span>
          </div>
          {item.analysis ? <p>{item.analysis}</p> : <p className="pr-outcome-detail-empty">No supporting note for this outcome.</p>}
        </article>
      ))}
    </div>
  );
};

const CommunityMatchContext = ({ context, home, away }) => {
  if (!context?.homeTeam && !context?.awayTeam && !context?.standings) return null;
  const homeData = context?.homeTeam || home || {};
  const awayData = context?.awayTeam || away || {};
  const homeName = homeData?.name || home?.name || "Home";
  const awayName = awayData?.name || away?.name || "Away";
  const standings = context?.standings || {};

  return (
    <section className="pr-community-context">
      <div className="pr-panel-head">
        <TrendingUp size={14} />
        <span>Fixture context</span>
      </div>
      <div className="pr-community-context-grid">
        <FormDots team={homeData} label={homeName} />
        <FormDots team={awayData} label={awayName} />
      </div>
      {(standings.home || standings.away) ? (
        <div className="pr-community-standings">
          {[[homeName, standings.home], [awayName, standings.away]].map(([name, row]) => (
            <div key={name}>
              <span>{name}</span>
              <StatCell label="Rank" value={row?.rank} />
              <StatCell label="Pts" value={row?.points} />
              <StatCell label="GD" value={row?.goalsDiff} />
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
};

const CommunityPredictionRoom = ({
  tips = [],
  userId,
  context,
  home,
  away,
  matchStarted,
  onUnlockCommunity,
  unlockPending,
  isAuthenticated,
  focusTipId = null,
  onNotify,
}) => {
  const sortedTips = useMemo(
    () =>
      [...tips].sort((a, b) => {
        const aVis = canViewCommunity(a, userId) ? 1 : 0;
        const bVis = canViewCommunity(b, userId) ? 1 : 0;
        if (aVis !== bVis) return bVis - aVis;
        const aConf = Number(leadOutcome(a)?.confidence) || 0;
        const bConf = Number(leadOutcome(b)?.confidence) || 0;
        return bConf - aConf;
      }),
    [tips, userId]
  );

  // Multiple tipster drawers can stay open at once.
  const [openIds, setOpenIds] = useState(() => {
    const initial = new Set();
    if (focusTipId) initial.add(String(focusTipId));
    return initial;
  });
  const tipIdsKey = sortedTips.map((t) => t._id).join("|");

  useEffect(() => {
    setOpenIds((prev) => {
      const valid = new Set(sortedTips.map((t) => String(t._id)));
      const next = new Set([...prev].filter((id) => valid.has(String(id))));
      if (focusTipId && valid.has(String(focusTipId))) {
        next.add(String(focusTipId));
      }
      return next;
    });
  }, [tipIdsKey, focusTipId]); // eslint-disable-line react-hooks/exhaustive-deps -- prune when tip list changes

  const openCount = openIds.size;
  const allOpen = sortedTips.length > 0 && openCount === sortedTips.length;

  const toggleTip = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const hideAll = () => setOpenIds(new Set());
  const openAll = () => setOpenIds(new Set(sortedTips.map((t) => String(t._id))));

  const freeCount = tips.filter((t) => !t.isPremium).length;
  const premiumCount = tips.length - freeCount;

  return (
    <div className="pr-community-room">
      <CommunityMatchContext context={context} home={home} away={away} />

      <div className="pr-section-eyebrow pr-tipster-eyebrow">
        <strong>Tipster slips</strong>
        <i className="pr-section-rule" aria-hidden />
        <em>{sortedTips.length}</em>
        {(freeCount || premiumCount) ? (
          <span className="pr-tipster-mix">
            {freeCount ? `${freeCount} free` : null}
            {freeCount && premiumCount ? " · " : null}
            {premiumCount ? `${premiumCount} premium` : null}
          </span>
        ) : null}
        {sortedTips.length > 1 ? (
          <span className="pr-tipster-bulk">
            {openCount > 0 ? (
              <button type="button" onClick={hideAll}>
                Hide all
              </button>
            ) : null}
            {!allOpen ? (
              <button type="button" onClick={openAll}>
                Open all
              </button>
            ) : null}
          </span>
        ) : null}
      </div>

      <div className="pr-tipster-list">
        {sortedTips.map((tip) => (
          <TipsterSlip
            key={tip._id}
            tip={tip}
            fixture={{ teams: { home, away } }}
            userId={userId}
            matchStarted={matchStarted}
            expanded={openIds.has(String(tip._id))}
            onToggle={() => toggleTip(tip._id)}
            onUnlock={onUnlockCommunity}
            unlockPending={unlockPending}
            isAuthenticated={isAuthenticated}
            onNotify={onNotify}
          />
        ))}
      </div>
    </div>
  );
};

const LockedAI = ({ prediction, pricing, userId, unlockPending, onUnlock, matchStarted }) => {
  const busy = unlockPending === prediction?._id;
  return (
    <section className="pr-panel pr-locked-ai">
      <div className="pr-panel-head">
        <Lock size={14} />
        <span>AI package locked</span>
      </div>
      <p>
        {matchStarted
          ? "This match has started — AI unlock is no longer available."
          : "Unlock to reveal the edge matrix, H2H trace, team pressure and advice."}
      </p>
      {!matchStarted ? (
        <button type="button" disabled={!userId || busy} onClick={() => onUnlock?.(prediction._id)}>
          {busy ? "Unlocking..." : pricing?.isFree ? "Unlock free" : `Unlock · ${pricing?.paidLabel || "250 COIN"}`}
        </button>
      ) : null}
    </section>
  );
};

const ChallengeActions = ({ entry, p2pOffers, isAuthenticated, matchStarted }) => {
  if (!entry || matchStarted) return null;

  const openOffer = p2pOffers?.[0] || null;
  const openCount = p2pOffers?.length || 0;
  const acceptTo = openOffer?._id ? `/app/p2p?w=${encodeURIComponent(openOffer._id)}` : "/app/p2p";
  const createState = {
    fixture: p2pFixtureForEntry(entry),
    league: p2pLeagueForEntry(entry),
  };

  return (
    <section className="pr-challenge-actions" aria-label="P2P challenge actions">
      {openOffer ? (
        <Link
          className="pr-challenge-btn pr-challenge-btn-primary"
          to={isAuthenticated ? acceptTo : "/app/login"}
          state={isAuthenticated ? undefined : { from: acceptTo }}
        >
          <Swords size={13} />
          Accept challenge
          {openCount > 1 ? <em>{openCount}</em> : null}
        </Link>
      ) : null}

      <Link
        className="pr-challenge-btn pr-challenge-btn-secondary"
        to={isAuthenticated ? "/app/p2p/create" : "/app/login"}
        state={isAuthenticated ? createState : { from: "/app/p2p/create" }}
      >
        <Plus size={13} />
        Create challenge
      </Link>
    </section>
  );
};

const PredictionRoomDetail = ({
  entry,
  user,
  userId,
  p2pOffers = [],
  onUnlock,
  onUnlockCommunity,
  unlockPending,
  pricing,
  isAuthenticated,
  onBackToList,
  onBackToOverview,
  initialSource = null,
  focusTipId = null,
  onNotify,
}) => {
  const favoriteTeam = user?.favoriteTeam;
  const [countdown, setCountdown] = useState("");
  const [source, setSource] = useState(null);
  const ts = entry?.ts;
  const started = entry ? isMatchStarted(entry.fixture) : false;
  const fixtureId = entry?.fixtureId;

  useEffect(() => {
    if (!ts || started) {
      setCountdown("");
      return undefined;
    }
    const tick = () => setCountdown(formatCountdownDetailed(ts));
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [ts, started]);

  useEffect(() => {
    if (!entry) {
      setSource(null);
      return;
    }
    if (initialSource === "crowd" && (entry.consensus?.count || 0) > 0) {
      setSource("crowd");
    } else if (initialSource === "ai" && entry.ai) {
      setSource("ai");
    } else if (entry.ai) setSource("ai");
    else if (entry.consensus?.count) setSource("crowd");
    else setSource(null);
  }, [fixtureId, initialSource]); // eslint-disable-line react-hooks/exhaustive-deps -- reset source when fixture changes

  const agreement = useMemo(
    () => (entry ? buildOutcomeAgreement(entry, userId) : null),
    [entry, userId]
  );

  if (!entry) {
    return (
      <section className="pr-room-detail pr-room-idle">
        <p className="pr-eyebrow">Prediction Intelligence</p>
        <h1>Select a fixture</h1>
        <p>Open a match, then choose AI or Crowd — each source stays separate.</p>
      </section>
    );
  }

  const fixture = entry.fixture || {};
  const home = fixture.teams?.home || {};
  const away = fixture.teams?.away || {};
  const showsFavoriteStar = fixtureHasFavoriteTeam(fixture, favoriteTeam);
  const ai = entry.ai;
  const hasAI = Boolean(ai);
  const hasCrowd = (entry.consensus?.count || 0) > 0;
  const activeSource = source || (hasAI ? "ai" : hasCrowd ? "crowd" : null);
  const aiLocked = hasAI && !ai.isUnlocked;
  const communityContext = entry.community?.find((tip) => tip.homeTeam || tip.awayTeam || tip.standings);
  const pred = ai?.predictions || {};
  const percent = pred.percent || entry.aiReadout?.percent || {};
  const scoreLean =
    pred.goals?.home != null || pred.goals?.away != null
      ? `${clean(pred.goals?.home || "0")}:${clean(pred.goals?.away || "0")}`
      : null;
  const live = entry.status?.tone === "live";
  const statusShort = live
    ? "LIVE"
    : entry.status?.label || fixture.status?.short || "NS";
  const kickLabel = ts
    ? `${formatAppDate(new Date(Number(ts) * 1000))} · ${formatAppTime(new Date(Number(ts) * 1000))}`
    : "Kickoff TBD";
  const timeLabel = live
    ? "LIVE"
    : started
      ? kickLabel
      : countdown
        ? `in ${countdown}`
        : kickLabel;

  const advice = ai ? pred.advice || aiAdviceLabel(ai) : null;
  const calls = ai
    ? {
        score: scoreLean,
        goals: pred.underOver ? clean(pred.underOver) : null,
        winDraw: pred.winOrDraw == null ? "—" : pred.winOrDraw ? "Yes" : "No",
      }
    : null;

  return (
    <section className="pr-room-detail" aria-label="Prediction intelligence room">
      {onBackToList ? (
        <button type="button" className="pr-mobile-back" onClick={onBackToList}>
          <ChevronLeft size={14} />
          All predictions
        </button>
      ) : null}

      <div className="pr-room-scroll">
        {onBackToOverview ? (
          <button
            type="button"
            className="pr-overview-back"
            onClick={onBackToOverview}
          >
            <LayoutGrid size={11} />
            Prediction overview
          </button>
        ) : null}

        <header className={`pr-match-slip ${live ? "pr-match-slip-live" : ""}`}>
          <div className="pr-match-slip-body">
            <p className="pr-detail-eyebrow">Bond Tips · Fixture</p>
            <div className="pr-hero-meta">
              <span>
                {entry.league?.name || "League"}
                {showsFavoriteStar ? <FavoriteTeamStar size={9} className="pr-hero-fav-star" /> : null}
              </span>
              <span className="pr-hero-meta-end">
                <em className={live ? "pr-ov-live-tag" : ""}>{timeLabel}</em>
                <ShareIconButton
                  className="bb-share-icon-sm"
                  size={12}
                  url={buildPredictionFixtureShareLink(fixtureId)}
                  text={buildPredictionFixtureShareText(entry)}
                  title="BetBond Predictions"
                  ariaLabel="Share this prediction"
                  onShared={(mode) =>
                    onNotify?.({
                      message: mode === "copied" ? "Link copied." : "Shared.",
                    })
                  }
                  onError={(message) => onNotify?.({ message })}
                />
              </span>
            </div>
            <div className="pr-hero-match">
              <TeamCrest team={home} align="home" favoriteTeam={favoriteTeam} />
              <div className="pr-hero-center">
                <span className={`pr-hero-status ${live ? "pr-hero-status-live" : ""}`}>
                  {statusShort}
                </span>
              </div>
              <TeamCrest team={away} align="away" favoriteTeam={favoriteTeam} />
            </div>
          </div>
        </header>

        <SourceCompare
          entry={entry}
          activeSource={activeSource}
          onSelectSource={setSource}
          agreement={agreement}
        />

        {activeSource === "ai" && hasAI ? (
          <div className="pr-source-pane" role="tabpanel" aria-label="AI prediction">
            <div className="pr-primary-grid">
              <section className="pr-panel pr-lens-panel">
                <div className="pr-panel-head">
                  <Gauge size={14} />
                  <span>AI prediction</span>
                </div>
                <ProbabilityLens
                  percent={percent}
                  homeName={home.name || "Home"}
                  awayName={away.name || "Away"}
                  locked={aiLocked}
                  calls={calls}
                />
              </section>

              {advice && !aiLocked ? (
                <section className="pr-panel pr-advice-panel">
                  <div className="pr-panel-head">
                    <MessageSquare size={14} />
                    <span>Betting advice</span>
                  </div>
                  <p>{advice}</p>
                </section>
              ) : (
                <section className="pr-panel pr-advice-panel pr-advice-muted">
                  <div className="pr-panel-head">
                    <MessageSquare size={14} />
                    <span>Betting advice</span>
                  </div>
                  <p>
                    {started
                      ? "This match has started — betting advice is no longer available to unlock."
                      : "Unlock the AI package for the betting advice."}
                  </p>
                </section>
              )}
            </div>

            {aiLocked ? (
              <LockedAI
                prediction={ai}
                pricing={pricing}
                userId={userId}
                unlockPending={unlockPending}
                onUnlock={onUnlock}
                matchStarted={started}
              />
            ) : (
              <EvidenceBoard prediction={ai} home={ai?.homeTeam || home} away={ai?.awayTeam || away} />
            )}
          </div>
        ) : null}

        {activeSource === "crowd" && hasCrowd ? (
          <div className="pr-source-pane" role="tabpanel" aria-label="Crowd tips">
            <OutcomeAgreementBoard agreement={agreement} />
            <CommunityPredictionRoom
              tips={entry.community}
              userId={userId}
              context={communityContext}
              home={home}
              away={away}
              matchStarted={started}
              onUnlockCommunity={onUnlockCommunity}
              unlockPending={unlockPending}
              isAuthenticated={isAuthenticated}
              focusTipId={focusTipId}
              onNotify={onNotify}
            />
          </div>
        ) : null}

        {!hasAI && !hasCrowd ? (
          <section className="pr-panel">
            <p className="pr-empty-copy">No AI model or crowd tips on this fixture yet.</p>
          </section>
        ) : null}

        <ChallengeActions
          entry={entry}
          p2pOffers={p2pOffers}
          isAuthenticated={isAuthenticated}
          matchStarted={started}
        />
      </div>
    </section>
  );
};

export default PredictionRoomDetail;
